import { useEffect, useState } from 'react';
import { RedirectMechanismType } from '@graphql/generated';
import { useClientReference } from '@shared/hooks/useClientReference';
import { encodeData, XDataInput } from '@shared/utils/encodeData';
import { useRetainSupportedParamsInStaysRedirectUrl } from '@shared/hooks/useRetainSupportedStaysParams';
import { usePartnerHotelQueryParams } from '@apps/guest/routes/HotelBooking/hooks/usePartnerHotelQueryParams';
import { getValidBookingLink } from '@apps/guest/routes/HotelBooking/HotelBooking.utils';
import {
  convertToBERedirectUrl,
  convertToCustomOrHotelUrl,
  convertToRedirectTelemetry,
  joyHotelDiscountCodes,
} from '@apps/guest/routes/HotelBooking/components/PartnerHotelRedirect/PartnerHotelRedirect.utils';
import { useNuiteeLanguage } from '@shared/hooks/useNuiteeLanguage';
import { useTravelPlacesPricingAndBookingDetailsFragment } from '@shared/hooks/useTravelPlacesPricingAndBookingDetails';
import { useTravelWhiteLabelCurrency } from '@shared/hooks/useTravelWhiteLabelCurrency';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { AccommodationCombinedType } from '../components/Accommodations/Accommodation.types';
import { GuestPartnerHotelBookingRedirectParams } from '@apps/guest/routes/HotelBooking/HotelBooking.types';
import { useValidateHasSafeLink } from './useValidateHasSafeLink';
import { extractHotelRedirects } from '@shared/utils/extractHotelRedirects';
import { JoyHotelQueryStringParameters, useHotelDiscount } from '@shared/components/DiscountsProvider/hotels/HotelsDiscountsProvider';
import { useAccommodationCurrency } from '../contexts/AccommodationCurrencyContext';

/**
 * Hook for transforming partner hotel redirect data into usable link/telemetry payloads.
 * @param {string} id - Accommodation or hotel ID
 * @returns {{
 *   transformPartnerHotelRedirect: (finalRedirectTo?: 'hotelSearchPageUrl' | 'hotelPageUrl') => {
 *     pricingInfo?: pricingInfo;
 *     safeLink?: string;
 *     redirectUrl?: URL;
 *     telemetryData?: object;
 *     isOpenInterceptorDialog?: boolean;
 *   };
 *   isLoading: boolean;
 * }}
 */
const useTransformPartnerHotelRedirect = (id: string) => {
  const hotelQueryParams = usePartnerHotelQueryParams(id);
  const joyHotelDiscount = useHotelDiscount();
  const { retainSupportedParamsInStaysRedirectUrl } = useRetainSupportedParamsInStaysRedirectUrl();

  const { data, loading: loadingData } = useTravelPlacesPricingAndBookingDetailsFragment(id, 'accommodationPlace');

  const { clientReference, loadingClientReference } = useClientReference();
  const language = useNuiteeLanguage();
  const eventCurrency = useAccommodationCurrency();
  const currency = useTravelWhiteLabelCurrency(eventCurrency);

  const isLoading = loadingClientReference || loadingData;

  /**
   * Transforms hotel redirect info and returns appropriate destination or dialog trigger.
   * @param {'hotelSearchPageUrl' | 'hotelPageUrl'} [finalRedirectTo] - Optional override for redirect target
   */
  const transformPartnerHotelRedirect = useEventCallback((finalRedirectTo?: 'hotelSearchPageUrl' | 'hotelPageUrl') => {
    if (isLoading) return { pricingInfo: undefined, safeLink: undefined, redirectUrl: undefined, telemetryData: undefined, isOpenInterceptorDialog: undefined };

    const { source, utmParams, discountCode } = hotelQueryParams;

    const redirectMechanisms = data?.redirectMechanisms;
    const pricingInfo = data?.pricingInfo;

    // discountCode is originally used for promotionals to nuitee
    const { nuiteeCode = discountCode, nativeCode } = joyHotelDiscountCodes(joyHotelDiscount);

    const { hotelPageUrl, hotelSearchPageUrl } = extractHotelRedirects(redirectMechanisms);
    const xData = nuiteeCode ? encodeData<XDataInput>({ discount: { code: nuiteeCode } }) : undefined;
    const hasHotelSearchPageUrl = finalRedirectTo === 'hotelSearchPageUrl';
    const redirectData = hasHotelSearchPageUrl && hotelSearchPageUrl ? hotelSearchPageUrl : hotelPageUrl;

    const accommodationPlace = data && 'accommodationPlace' in data && data.accommodationPlace;
    const googleMaps = accommodationPlace
      ? {
          lat: accommodationPlace.lat,
          lng: accommodationPlace.long,
          gplid: accommodationPlace.googlePlaceId,
          name: accommodationPlace.name,
          address: accommodationPlace.address,
          zentrumHubId: accommodationPlace.zhId,
        }
      : undefined;

    const telemetryPayload = convertToRedirectTelemetry({
      clientReference,
      redirectData,
      pricingInfo,
      googleMaps,
      telemetryName: 'guestPartnerHotelBookingRedirect',
      source,
      utmParams,
      hasHotelSearchPageUrl: !!hotelSearchPageUrl,
    });

    // A: Show Interceptor Dialog
    if (hotelSearchPageUrl && !finalRedirectTo) {
      return { pricingInfo, isOpenInterceptorDialog: true };
    }

    // B: Custom redirect
    if (redirectData?.type === RedirectMechanismType.custom && redirectData?.url) {
      const enhancedUrl = retainSupportedParamsInStaysRedirectUrl(redirectData.url);
      const hotelUrl = convertToCustomOrHotelUrl(enhancedUrl, clientReference, currency, redirectData, false);
      telemetryPayload.destinationUrl = hotelUrl;
      return { safeLink: hotelUrl, telemetryData: telemetryPayload, pricingInfo };
    }

    // C: Native checkout
    if (redirectData?.type === RedirectMechanismType.joyNativeCheckout && redirectData?.url) {
      const redirectUrl = new URL(redirectData.url);
      if (nativeCode) {
        redirectUrl.searchParams.set(JoyHotelQueryStringParameters.joyHotelDiscountCode, nativeCode);
      }
      redirectUrl.searchParams.set('currency', currency);
      telemetryPayload.destinationUrl = redirectUrl;
      return { redirectUrl, telemetryData: telemetryPayload, pricingInfo };
    }

    // D: BE redirect fallback
    if (redirectData?.url) {
      const redirectUrl = convertToBERedirectUrl({ redirectData, clientReference, currency, language, xData, placeId: googleMaps?.gplid, name: googleMaps?.name });
      telemetryPayload.destinationUrl = redirectUrl;
      return { redirectUrl, telemetryData: telemetryPayload, pricingInfo };
    }

    return { pricingInfo };
  });

  return { transformPartnerHotelRedirect, data, isLoading };
};

type RedirectLinkState = Readonly<{
  telemetryData: Partial<GuestPartnerHotelBookingRedirectParams>;
  redirectUrl: URL;
}>;
interface RedirectState {
  normalLink?: RedirectLinkState;
  soldOutLink?: RedirectLinkState;
  safeLink?: RedirectLinkState;
}

/**
 * Hook to handle all partner hotel redirect flows.
 * Includes logic for sold-out dialogs, safe link dialog, redirect links, and telemetry.
 *
 * @param {string} id - Accommodation or hotel ID
 * @param {string} bookingLink - Room block or white label booking link
 * @returns {{
 *   isLoading: boolean;
 *   pricingInfo: pricingInfo;
 *   safeLink?: string;
 *   isOpenInterceptorDialog: boolean;
 *   setSafeLink: (url?: string) => void;
 *   partnerHotelRedirect: (e: React.MouseEvent<HTMLAnchorElement>, type: AccommodationCombinedType) => void;
 *   handleOnAccept: () => void;
 *   handleOnDecline: () => void;
 * }}
 */
export const usePartnerHotelRedirect = (id: string, type: AccommodationCombinedType, bookingLink: Maybe<string>) => {
  const [redirectState, setRedirectState] = useState<RedirectState>();
  const [safeLink, setSafeLink] = useState(false);
  const [isOpenInterceptorDialog, setIsOpenInterceptorDialog] = useState(false);

  const { transformPartnerHotelRedirect, data, isLoading } = useTransformPartnerHotelRedirect(id);
  const pricingInfo = data?.pricingInfo;

  const [validateHasSafeLink, loadingSafeLinkFeatureFlags] = useValidateHasSafeLink();
  /**
   * Compute redirect link for Joy room block with safe link and sold out feature.
   */
  const roomBlockRedirect = useEventCallback((bookingLink: Maybe<string>) => {
    const validBookingLink = getValidBookingLink(bookingLink ?? '');
    if (validBookingLink) {
      const { isSafeLink, url } = validateHasSafeLink(validBookingLink);
      if (isSafeLink) {
        setRedirectState({ normalLink: { redirectUrl: url, telemetryData: { roomBlockUrl: validBookingLink, roomBlockSuccess: !!validBookingLink } } });
      } else {
        setRedirectState({ safeLink: { redirectUrl: validBookingLink, telemetryData: { roomBlockUrl: validBookingLink, roomBlockSuccess: !!validBookingLink } } });
      }
    }
  });

  /**
   * Compute redirect link for hotel with safe link and sold out feature.
   */
  const hotelRedirect = useEventCallback(() => {
    const redirectData = transformPartnerHotelRedirect();

    if (redirectData.isOpenInterceptorDialog) {
      const normalLink = transformPartnerHotelRedirect('hotelPageUrl');
      const soldOutLink = transformPartnerHotelRedirect('hotelSearchPageUrl');
      if (normalLink.redirectUrl && soldOutLink.redirectUrl) {
        setRedirectState({ normalLink, soldOutLink });
      }
    } else if (redirectData.safeLink) {
      const { isSafeLink, url } = validateHasSafeLink(redirectData.safeLink);
      if (isSafeLink) {
        setRedirectState({ normalLink: { ...redirectData, redirectUrl: url } });
      } else {
        setRedirectState({ safeLink: { ...redirectData, redirectUrl: url } });
      }
    } else if (redirectData.redirectUrl) {
      setRedirectState({ normalLink: redirectData });
    }
  });

  // Start computing redirect link on page load.
  useEffect(() => {
    if (isLoading || loadingSafeLinkFeatureFlags) return;

    if (type === AccommodationCombinedType.Roomblock) {
      roomBlockRedirect(bookingLink);
    } else {
      hotelRedirect();
    }
  }, [hotelRedirect, isLoading, loadingSafeLinkFeatureFlags, roomBlockRedirect, type, bookingLink, data]);

  const convertStateToObject = useEventCallback(<T>(value: T, setter: (val: T) => void) => ({ get: () => value, set: setter }));

  return {
    isLoading: isLoading || loadingSafeLinkFeatureFlags,
    pricingInfo,
    redirectState,
    safeLinkState: convertStateToObject(safeLink, setSafeLink),
    interceptorDialogState: convertStateToObject(isOpenInterceptorDialog, setIsOpenInterceptorDialog),
  };
};
